import React, { useEffect, useState } from "react";
import _ from "underscore";
import Language from "../lib/Language";
import ArrayInput from "./ArrayInput";
import { isSameTrack } from "./preferences/PrefsCoreAPI.utils";
import { isSameValue, TrackVariation, TrackVariationSet } from "../lib/ActivityUtils";
import API from "../lib/TimeEditAPI";
import ContextMenu from "../lib/ContextMenu";
import Log from "../lib/Log";
import { Table } from "@timeedit/tecore-table";
import { MillenniumDateTime, SimpleDateFormat } from "@timeedit/millennium-time";
import NamedWeek from "../lib/NamedWeek";
import { useAppContext } from "../contexts/AppContext";

type WeekRange = {
    start: number;
    end: number;
};

const toCustomWeekName = (yearAndWeek: number, customWeekNames: NamedWeek[]): string => {
    const weekNumber = yearAndWeek % 100;
    if (!customWeekNames.length) {
        return `${weekNumber}`;
    }
    const namedWeek = Language.getCustomWeekFromISOWeek(
        Number(`${yearAndWeek}`.substring(2)),
        customWeekNames
    );

    return namedWeek?.getShortestName() ?? `${weekNumber}`;
};

const presentAsRanges = (weeks, customWeekNames: NamedWeek[]) => {
    const ranges: WeekRange[] = [];
    let nextRange = { start: 0, end: 0 };
    weeks.forEach((week) => {
        if (nextRange.start === 0) {
            nextRange.start = week;
            nextRange.end = week;
        } else if (nextRange.end + 1 === week) {
            nextRange.end = week;
        } else {
            ranges.push(nextRange);
            nextRange = { start: week, end: week };
        }
    });
    if (nextRange.start !== 0) {
        ranges.push(nextRange);
    }
    return ranges
        .map(({ start, end }) => ({
            start: toCustomWeekName(start, customWeekNames),
            end: toCustomWeekName(end, customWeekNames),
        }))
        .map((range) => {
            if (range.start === range.end) {
                return `${range.start}`;
            }
            return `${range.start}-${range.end}`;
        })
        .join(",");
};

const getName = (value, objectNames) => {
    if (objectNames[value]) {
        return objectNames[value];
    }
    return value;
};

const presentDiffValue = (activityValue, objectNames) => {
    if (Array.isArray(activityValue.value)) {
        return activityValue.value.map((vl) => getName(vl, objectNames)).join(", ");
    } else if (activityValue.value.categories) {
        return "Filter";
    }
    return getName(activityValue.value, objectNames);
};

const presentVariation = (track: TrackVariation, parent: TrackVariation, objectNames) => {
    const trackActivity = track.activities[0];
    const parentActivity = parent.activities[0];
    const differences: any = [];
    trackActivity.values.forEach((trackValue) => {
        let isDifference = true;
        parentActivity.values.forEach((parentValue) => {
            if (isSameValue(trackValue, parentValue)) {
                isDifference = false;
            }
        });
        if (isDifference) {
            differences.push(trackValue);
        }
    });
    return differences.map((diff) => presentDiffValue(diff, objectNames)).join(", ");
};

const getProgress = (
    track: TrackVariation,
    mapData,
    allTracks: TrackVariationSet | undefined,
    objectNames,
    customWeekNames: NamedWeek[]
) => {
    const weeks = new Set(
        track.activities.map((act) =>
            parseInt(`${act.startDate.substring(0, 4)}${String(act.week).padStart(2, "0")}`, 10)
        )
    );
    const numWeeks = weeks.size;
    const mappedIds = _.uniq([
        ...mapData
            .map((data) => data.activityId)
            .filter((id) => _.any(track.activities, (activity) => activity.activityId === id)),
        ...track.activities.filter((entry) => entry.reservationId).map((entry) => entry.activityId),
    ]);
    const done = mappedIds.length / numWeeks;
    const total = track.activities.length / numWeeks;
    const diffText =
        track.variationIndex !== 0 && allTracks
            ? presentVariation(track, allTracks[`${track.parentIndex}:1`], objectNames)
            : null;
    // Done = done === total
    // Partial = done > 0 && done !== total
    if (diffText) {
        return `(${done}/${total}) (${presentAsRanges(
            [...weeks].sort(),
            customWeekNames
        )}) ${diffText}`;
    }
    return `(${done}/${total}) (${presentAsRanges([...weeks].sort(), customWeekNames)})`;
};

const getRowPrefix = (variation: TrackVariation) => {
    if (variation.variationIndex === 0) {
        return variation.parentIndex;
    }
    return "";
};

const loadReservations = (reservationIds, callback) => {
    API.exportReservations(reservationIds, false, (result) => {
        callback(result);
    });
};

const extractReservations = (tracks: TrackVariationSet, mappedResult: any) => {
    const reservationIds: string[] = [];
    Object.keys(tracks).forEach((trackKey) => {
        if (
            tracks[trackKey].activities.every((activity) => {
                return mappedResult.some((result) => result.activityId === activity.activityId);
            })
        ) {
            reservationIds.push(
                mappedResult.find(
                    (result) => result.activityId === tracks[trackKey].activities[0].activityId
                ).reservationId
            );
            //reservationIds.push(parseInt(tracks[trackKey].activities[0].reservationId, 10));
        }
    });
    return reservationIds.map((rId) => parseInt(rId, 10));
};

const loadObjectNames = (tracks: TrackVariationSet, callback) => {
    let extIds: string[] = [];
    Object.keys(tracks).forEach((trackKey) => {
        tracks[trackKey].activities.forEach((activity) => {
            activity.values.forEach((value) => {
                if (value.type === "object" && !value.value.categories) {
                    if (Array.isArray(value.value)) {
                        value.value.forEach((vl) => extIds.push(vl));
                    } else {
                        extIds.push(value.value);
                    }
                }
            });
        });
    });
    API.getObjectNamesByExtid([...new Set(extIds)], false, (result) => {
        const nameLookup = {};
        result.forEach((namedObject) => {
            const name = namedObject.fields
                .map((field) => (field.values ? field.values.join(", ") : ""))
                .join(", ");
            if (name && name !== "") {
                nameLookup[namedObject.extid] = name;
            }
        });
        callback(nameLookup);
    });
};

const COLUMNS = [
    { id: 0, name: "Track", sortable: false },
    { id: 1, name: "Status", sortable: false },
    { id: 2, name: "Weeks", sortable: false },
    { id: 3, name: "Variation", sortable: false },
    { id: 4, name: "Scheduled time", sortable: false },
];

const getAllColumns = () => {
    return COLUMNS;
};

const getColumns = () => {
    return COLUMNS;
};

const getColumnWidths = () => {
    return [];
};

const trackCellRenderer = (cellData) => {
    if (cellData.cellData === "") {
        return "";
    }
    return (
        <div>
            {" "}
            <div className="trackIconCell" />
            {cellData.cellData}
        </div>
    );
};

const statusCellRenderer = (cellData) => {
    const status =
        cellData.cellData === STATUS.SCHEDULED.id ? STATUS.SCHEDULED : STATUS.NOT_SCHEDULED;
    if (cellData.cellData !== STATUS.SCHEDULED.id) {
        return cellData.cellData;
    }
    return (
        <div>
            <div className={`statusCell ${status.id}`} title={status.title}>
                {status.title}
            </div>
        </div>
    );
};

const getTime = (time, isEnd) => {
    return SimpleDateFormat.format(
        time,
        isEnd ? Language.getDateFormat("date_f_hh_mm_end") : Language.getDateFormat("date_f_hh_mm")
    );
};

const presentTime = (variation: TrackVariation, reservations) => {
    let activityId = variation.activities[0].activityId;
    let reservationKey = Object.keys(reservations).find(
        (rKey) => reservations[rKey].activityId === activityId
    );
    let reservation = reservationKey === undefined ? undefined : reservations[reservationKey];
    if (!reservation) {
        return "";
    }
    return `${getTime(new MillenniumDateTime(reservation.begin), false)}-${getTime(
        new MillenniumDateTime(reservation.end),
        true
    )}`;
};

const renderTrackList = (
    props,
    objectNames,
    useTable,
    getTableRow,
    registerSetSelected,
    selectedColumns,
    columnWidths,
    onTableColumnsChange,
    onTableColumnsWidthChange,
    customWeekNames: NamedWeek[]
) => {
    if (!useTable) {
        const HEIGHT_MODIFIER = 70;
        return (
            <ArrayInput
                defaultSize={7}
                height={props.outerHeight - HEIGHT_MODIFIER}
                onUpdate={props.onUpdate}
                defaultValue={Object.entries(props.tracks as TrackVariationSet).map((entry) => ({
                    label: `${getRowPrefix(entry[1])} ${getProgress(
                        entry[1],
                        props.mappedResult,
                        props.tracks,
                        objectNames,
                        customWeekNames
                    )}`,
                    value: entry[0],
                    selected: isSameTrack(props.currentTrack, entry[1]),
                }))}
            />
        );
    }
    const cellRenderers = {};
    cellRenderers[0] = trackCellRenderer;
    cellRenderers[1] = statusCellRenderer;
    return (
        <Table
            cellRenderers={cellRenderers}
            language={Language}
            log={Log}
            presentModal={props.presentModal}
            contextMenu={ContextMenu}
            isRowLoaded={() => true}
            allColumns={getAllColumns()}
            columns={selectedColumns}
            columnWidths={columnWidths}
            onColumnChange={onTableColumnsChange}
            onColumnWidthChange={onTableColumnsWidthChange}
            registerSetSelected={registerSetSelected}
            getTableRow={getTableRow}
            rowCount={Object.entries(props.tracks).length}
            onSelect={(index) => {
                props.onUpdate([Object.keys(props.tracks)[index]]);
            }}
            width={"100%"}
            highlight={Table.HIGHLIGHT.SINGLE}
        />
    );
};

const STATUS = {
    SCHEDULED: { id: "statusScheduled", title: "Scheduled" },
    NOT_SCHEDULED: { id: "statusNotScheduled", title: "Not scheduled" },
};

let _selectionFn: any = null;

const TrackPane = (props: {
    tracks: TrackVariationSet | undefined;
    mappedResult: any | undefined;
    onUpdate: any;
    currentTrack: TrackVariation;
    onCancel: any;
    outerHeight: number;
    numWeeks: number;
    presentModal: any;
}) => {
    const { customWeekNames } = useAppContext();
    const [objectNames, setObjectNames] = useState({});
    const [reservations, setReservations] = useState({});
    const [selectedColumns, setSelectedColumns] = useState(getAllColumns());
    const [columnWidths, setColumnWidths] = useState([]);

    useEffect(() => {
        if (!props.tracks) {
            return;
        }
        loadObjectNames(props.tracks!, (result) => {
            setObjectNames(result);
        });
        loadReservations(extractReservations(props.tracks!, props.mappedResult), (result) => {
            const reservationMap = {};
            result.forEach((res) => {
                reservationMap[res.id] = res;
            });
            setReservations(reservationMap);
        });
    }, [JSON.stringify(props.mappedResult)]);

    useEffect(() => {
        if (!props.tracks) {
            return;
        }
        if (!_selectionFn) {
            return;
        }
        const index = Object.keys(props.tracks).findIndex((trackKey) =>
            isSameTrack(props.tracks![trackKey], props.currentTrack)
        );
        _selectionFn(index);
    }, [JSON.stringify(props.currentTrack)]);

    useEffect(() => {
        API.getPreferences("trackColumns", undefined, undefined, (columnResult: any) => {
            if (!columnResult) {
                return;
            }
            try {
                const columns = JSON.parse(columnResult);
                setSelectedColumns(columns);
            } catch (ignore) {
                console.log(ignore);
            }
        });
        API.getPreferences("trackColumnWidths", undefined, undefined, (widthResult: any) => {
            if (!widthResult) {
                return;
            }
            try {
                const widths = JSON.parse(widthResult);
                setColumnWidths(widths);
            } catch (ignore) {
                console.log(ignore);
            }
        });
    }, []);

    if (!props.tracks || Object.keys(props.tracks).length === 0) {
        return null;
    }

    const getTableRow = ({ index }) => {
        const row = {};
        if (!props.tracks) {
            return row;
        }
        const variation = Object.entries(props.tracks)[index][1];
        const weeks = new Set(
            variation.activities.map((act) =>
                parseInt(`${act.startDate.substring(0, 4)}${String(act.week).padStart(2, "0")}`, 10)
            )
        );
        // const numWeeks = weeks.size;

        const total = variation.activities.length; // / numWeeks;
        const mappedIds = _.uniq([
            ...props.mappedResult
                .map((data) => data.activityId)
                .filter((id) =>
                    _.any(variation.activities, (activity) => activity.activityId === id)
                ),
            ...variation.activities
                .filter((entry) => entry.reservationId)
                .map((entry) => entry.activityId),
        ]);
        const done = mappedIds.length; // / numWeeks;

        getColumns().forEach((col) => {
            switch (col.id) {
                case 0:
                    row[col.name] = getRowPrefix(variation);
                    break;
                case 1:
                    if (done === total) {
                        row[col.name] = STATUS.SCHEDULED.id; //<div className="scheduled">Scheduled</div>;
                    } else if (done === 0) {
                        row[col.name] = `${done}/${total}`; //STATUS.NOT_SCHEDULED.id; //<div className="notScheduled">Not scheduled</div>;
                    } else {
                        row[col.name] = `${done}/${total}`;
                    }
                    break;
                case 2:
                    row[col.name] = presentAsRanges([...weeks].sort(), customWeekNames);
                    break;
                case 3: {
                    row[col.name] =
                        variation.variationIndex !== 0 && props.tracks
                            ? presentVariation(
                                  variation,
                                  props.tracks[`${variation.parentIndex}:1`],
                                  objectNames
                              )
                            : null;
                    break;
                }
                case 4:
                    row[col.name] = done !== total ? "" : presentTime(variation, reservations);
                    break;
                default:
                    row[col.name] = "";
                    break;
            }
        });
        return row;
    };

    const registerSetSelected = (fn) => {
        _selectionFn = fn;
    };

    const onTableColumnsChange = (columns) => {
        API.setPreferences("trackColumns", [JSON.stringify(columns)], _.noop);
        setSelectedColumns(columns);
    };

    const onTableColumnsWidthChange = (columnWidths) => {
        API.setPreferences("trackColumnWidths", [JSON.stringify(columnWidths)], _.noop);
        setColumnWidths(columnWidths);
    };

    const useTable = true;
    const trackList = renderTrackList(
        props,
        objectNames,
        useTable,
        getTableRow,
        registerSetSelected,
        selectedColumns,
        columnWidths,
        onTableColumnsChange,
        onTableColumnsWidthChange,
        customWeekNames
    );

    return (
        <div style={{ display: "flex" }}>
            <div className="listTopLine">
                <h1>Tracks</h1>
            </div>
            {trackList}
            <button className="deprecated prefsOperationButton cancel" onClick={props.onCancel}>
                {Language.get("dialog_cancel")}
            </button>
        </div>
    );
};

export default TrackPane;
